import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

export type JobStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'ERROR';

export interface Job {
  id: string;
  title: string;
  status: JobStatus;
  startTime: Date;
  endTime?: Date;
  error?: string;
  pollingFn?: () => Promise<JobStatus>;
  onComplete?: () => void;
  onError?: (error: string) => void;
}

interface JobsContextType {
  activeJobs: Job[];
  completedJobs: Job[]; // Includes errors
  addJob: (job: Omit<Job, 'id' | 'startTime' | 'status'> & { id?: string }) => string;
  updateJobStatus: (id: string, status: JobStatus, error?: string) => void;
  removeJob: (id: string) => void;
  clearCompletedJobs: () => void;
  markAsRead: () => void;
  hasUnread: boolean;
}

const JobsContext = createContext<JobsContextType | undefined>(undefined);

export const useJobs = () => {
  const context = useContext(JobsContext);
  if (!context) {
    throw new Error('useJobs must be used within a JobsProvider');
  }
  return context;
};

export const JobsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeJobs, setActiveJobs] = useState<Job[]>([]);
  const [completedJobs, setCompletedJobs] = useState<Job[]>([]); // History
  const [hasUnread, setHasUnread] = useState(false);
  
  // Ref to access current active jobs inside interval
  const activeJobsRef = useRef<Job[]>([]);
  
  // Sync ref
  useEffect(() => {
    activeJobsRef.current = activeJobs;
  }, [activeJobs]);

  const addJob = useCallback((jobData: Omit<Job, 'id' | 'startTime' | 'status'> & { id?: string }) => {
    const newJob: Job = {
      id: jobData.id || crypto.randomUUID(),
      startTime: new Date(),
      status: 'PROCESSING', // Default start status
      ...jobData,
    };
    
    // Avoid duplicates if ID provided
    setActiveJobs(prev => {
      if (prev.some(j => j.id === newJob.id)) return prev;
      return [...prev, newJob];
    });
    
    return newJob.id;
  }, []);

  const updateJobStatus = useCallback((id: string, status: JobStatus, error?: string) => {
    if (status === 'COMPLETED' || status === 'ERROR') {
      // Move to completed
      setActiveJobs(prev => {
        const job = prev.find(j => j.id === id);
        if (!job) return prev;
        
        const finishedJob = { ...job, status, endTime: new Date(), error };
        
        setCompletedJobs(prevCompleted => [finishedJob, ...prevCompleted].slice(0, 50)); // Keep last 50
        setHasUnread(true);
        
        // Execute callbacks
        if (status === 'COMPLETED' && job.onComplete) {
            try { job.onComplete(); } catch (e) { console.error("Error in onComplete", e); }
        }
        if (status === 'ERROR' && job.onError) {
            try { job.onError(error || 'Unknown error'); } catch (e) { console.error("Error in onError", e); }
        }
        
        return prev.filter(j => j.id !== id);
      });
    } else {
      // Update active
      setActiveJobs(prev => prev.map(job => 
        job.id === id ? { ...job, status } : job
      ));
    }
  }, []);

  const removeJob = useCallback((id: string) => {
    setActiveJobs(prev => prev.filter(j => j.id !== id));
  }, []);

  const clearCompletedJobs = useCallback(() => {
    setCompletedJobs([]);
    setHasUnread(false);
  }, []);

  const markAsRead = useCallback(() => {
    setHasUnread(false);
  }, []);

  // Global Poller
  useEffect(() => {
    const intervalId = setInterval(async () => {
      const jobsToPoll = activeJobsRef.current.filter(job => job.pollingFn);
      
      for (const job of jobsToPoll) {
        try {
          if (job.pollingFn) {
            const newStatus = await job.pollingFn();
            
            // Only update if status changed or is final
            if (newStatus !== job.status || newStatus === 'COMPLETED' || newStatus === 'ERROR') {
               updateJobStatus(job.id, newStatus);
            }
          }
        } catch (err: any) {
          // No marcar el job como ERROR si es un error de autenticación temporal (401/403)
          // Estos errores pueden ser temporales y no indican que el cálculo haya fallado
          const statusCode = err.response?.status || err.status;
          if (statusCode === 401 || statusCode === 403) {
            console.warn(`Error de autenticación en polling job ${job.id}, reintentando...`);
            // No actualizar el estado del job, seguir intentando
            continue;
          }
          
          // Para otros errores, marcar como ERROR solo si no es un error de red temporal
          console.error(`Error polling job ${job.id}:`, err);
          // Solo marcar como ERROR si es un error definitivo (no timeout, no network error)
          if (err.code !== 'ECONNABORTED' && err.code !== 'ERR_NETWORK') {
            updateJobStatus(job.id, 'ERROR', err.message || 'Error en polling');
          }
        }
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(intervalId);
  }, [updateJobStatus]); // Dependencies are stable

  return (
    <JobsContext.Provider value={{
      activeJobs,
      completedJobs,
      addJob,
      updateJobStatus,
      removeJob,
      clearCompletedJobs,
      markAsRead,
      hasUnread
    }}>
      {children}
    </JobsContext.Provider>
  );
};
